import { useId, useState } from 'react'
import { Alert, Button, DatePicker } from '@indipia/ui-core'
import { EditPencilIcon } from './EditPencilIcon'
import type { OrderScheduleResult } from '../../api/orderApi'
import { dateWarningMessages, formatOrderDatesLine, ORDER_MESSAGE, orderErrorMessage } from '../../constants/materialOrder.logic'

// 발주일·입고희망일 편집 — 변경 즉시 적용(C9, 적용 버튼 없음). 값이 없으면 FE가 계산해 채우지 않는다.
// 호출 화면이 onChange 안에서 useOrderActions.changeDates 를 실행해 C9 결과를 돌려주면 이 컴포넌트가 경고·영향을 표시한다.
// compact: 카드용 — 보기 모드(글자 + 수정 아이콘, 날짜 없으면 "발주일·입고일 미정" + 수정 아이콘) ↔ 편집 모드(DatePicker 2개 + [완료]).

type DatePatch = { orderDate?: string | null; deliveryDate?: string | null }

interface Props {
  taskId: number
  orderDate: string | null
  deliveryDate: string | null
  /** 공종 착공일(M/D 안내 표시용) */
  startDate: string
  compact?: boolean
  /** compact: 처음부터 편집 모드로 연다(카드가 보기 모드 줄에서 이미 수정 진입을 눌렀을 때) */
  startEditing?: boolean
  /** compact: [완료]를 눌렀을 때 호출(호출 쪽 보기 모드로 복귀) */
  onDone?: () => void
  disabled?: boolean
  /** 변경 값(null=비움). C9 실행 결과를 돌려주면 경고·영향 문구를 표시하고, 오류를 던지면 오류 문구를 표시한다 */
  onChange: (patch: DatePatch) => Promise<OrderScheduleResult | void>
}

export function OrderDateEditor({ taskId, orderDate, deliveryDate, startDate, compact = false, startEditing = false, onDone, disabled = false, onChange }: Props) {
  const uid = useId() // 카드와 팝업이 같은 taskId 로 동시에 열려도 DOM id 가 겹치지 않게 한다
  const [editing, setEditing] = useState(startEditing)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notices, setNotices] = useState<string[]>([])

  const apply = async (patch: DatePatch) => {
    setBusy(true)
    setError('')
    try {
      const res = await onChange(patch)
      setNotices(res ? dateWarningMessages(res.warnings, res.affected) : [])
    } catch (e) {
      setError(orderErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const off = disabled || busy

  const fields = (size: 'sm' | 'md') => (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex items-end gap-1">
        <DatePicker
          id={`order-date-${taskId}-${uid}`}
          label={ORDER_MESSAGE.orderDate}
          size={size}
          value={orderDate ?? ''}
          disabled={off}
          onChange={d => void apply({ orderDate: d })}
        />
        {orderDate && !off && (
          <Button variant="ghost" size="sm" aria-label={`${ORDER_MESSAGE.orderDate} ${ORDER_MESSAGE.clearDate}`} onClick={() => void apply({ orderDate: null })}>
            {ORDER_MESSAGE.clearDate}
          </Button>
        )}
      </div>
      <div className="flex items-end gap-1">
        <DatePicker
          id={`delivery-date-${taskId}-${uid}`}
          label={ORDER_MESSAGE.deliveryDate}
          size={size}
          value={deliveryDate ?? ''}
          disabled={off}
          onChange={d => void apply({ deliveryDate: d })}
        />
        {deliveryDate && !off && (
          <Button variant="ghost" size="sm" aria-label={`${ORDER_MESSAGE.deliveryDate} ${ORDER_MESSAGE.clearDate}`} onClick={() => void apply({ deliveryDate: null })}>
            {ORDER_MESSAGE.clearDate}
          </Button>
        )}
      </div>
    </div>
  )

  const feedback = (
    <>
      {error && <div className="mt-1"><Alert kind="error" role="alert">{error}</Alert></div>}
      {notices.length > 0 && (
        <div className="mt-1" aria-live="polite">
          <Alert kind="warning">
            {notices.map(n => <div key={n}>{n}</div>)}
          </Alert>
        </div>
      )}
    </>
  )

  if (!compact) {
    return (
      <div>
        {fields('md')}
        <div className="mt-1 text-xs" style={{ color: 'var(--shell-on-surface-2)' }}>{startDateHint(startDate)}</div>
        {feedback}
      </div>
    )
  }

  if (editing) {
    return (
      <div>
        {fields('sm')}
        <div className="mt-1 flex items-center gap-2">
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => { setEditing(false); setNotices([]); onDone?.() }}>{ORDER_MESSAGE.editDone}</Button>
          <span className="text-xs" style={{ color: 'var(--shell-on-surface-2)' }}>{startDateHint(startDate)}</span>
        </div>
        {feedback}
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-xs min-w-0" style={{ color: 'var(--shell-on-surface)' }}>{formatOrderDatesLine(orderDate, deliveryDate, startDate)}</span>
        {!disabled && (
          <Button variant="ghost" size="sm" aria-label={ORDER_MESSAGE.editDates} onClick={() => setEditing(true)}><EditPencilIcon /></Button>
        )}
      </div>
      {feedback}
    </div>
  )
}

function startDateHint(startDate: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(startDate)
  return m ? `착공 ${Number(m[1])}/${Number(m[2])}` : ''
}
